import { Vue, Prop } from 'vue-property-decorator';
import axios from 'axios';
import Resource from './Resource';

export default class OdataResource<TResourceType> extends Resource<TResourceType> {
    constructor(
        baseUrl: string,
        onResourceLoading: Function | undefined = undefined,
        onResourceLoadingFinished: Function | undefined = undefined,
        onError: Function | undefined = undefined,
        onResourcesLoaded: Function | undefined = undefined,
        onResourceLoaded: Function | undefined = undefined,
    ) {
        super();
        this.baseUrl = baseUrl;
        if (onResourceLoading) this.$on('resourceLoading', onResourceLoading);
        if (onResourceLoadingFinished) this.$on('resourceLoadingFinished', onResourceLoadingFinished);
        if (onError) this.$on('onError', onError);
        if (onResourcesLoaded) this.$on('resourcesLoaded', onResourcesLoaded);
        if (onResourceLoaded) this.$on('resourceLoaded', onResourceLoaded);
    }

    private get resourceUrl(): string {
        return this.baseUrl;
    }

    public async getResources(parametersString: string) {
        const resourceUrl = parametersString !== '' ? '?' + parametersString : '';
        const response = await this.getFromApi(resourceUrl);
        if (response !== undefined) {
            const resource = (response.data.value !== undefined
                ? response.data.value
                : response.data) as TResourceType[];
            this.resourcesLoaded(resource);
            return resource;
        }
    }

    public async getResource(id: number) {
        const parameterString = '(' + id + ')';
        const response = await this.getFromApi(parameterString);
        if (response !== undefined) {
            const resource = response.data as TResourceType;
            this.resourceLoaded(resource);
            return resource;
        }
    }

    public async createNewResource(resourceData: TResourceType, parametersString = '') {
        const resourceUrl = parametersString !== '' ? '?' + parametersString : '';
        const response = await this.postToApi(resourceUrl, resourceData);
        if (response !== undefined) {
            const resource = response.data as TResourceType;
            this.resourceCreated(resource);
            return resource;
        }
    }

    public async patchResource(id: string, resourceData: TResourceType) {
        const response = await this.patchToApi(`/${id}`, resourceData);
        if (response !== undefined) {
            const resource = response.data as TResourceType;
            this.resourcePatched(resource);
            return resource;
        }
    }

    private async getFromApi(parameterString: string) {
        this.resourceLoading({ resourceUrl: this.resourceUrl + parameterString, resourceData: {} });
        try {
            return await axios.get(this.resourceUrl + parameterString);
        } catch (exception) {
            const error = exception as Error;
            this.handleError(error);
        } finally {
            this.resourceLoadingFinished(this.resourceUrl + parameterString);
        }
    }

    private async postToApi(parameterString: string, resource: TResourceType) {
        this.resourceLoading({ resourceUrl: this.resourceUrl + parameterString, resourceData: resource });
        try {
            return await axios.post(this.resourceUrl + parameterString, resource);
        } catch (exception) {
            const error = exception as Error;
            this.handleError(error);
        } finally {
            this.resourceLoadingFinished(this.resourceUrl + parameterString);
        }
    }

    private async patchToApi(parameterString: string, resource: TResourceType) {
        this.resourceLoading({ resourceUrl: this.resourceUrl + parameterString, resourceData: resource });
        try {
            return await axios.patch(this.resourceUrl + parameterString, this.toPascalCaseObject(resource));
        } catch (exception) {
            const error = exception as Error;
            this.handleError(error);
        } finally {
            this.resourceLoadingFinished(this.resourceUrl + parameterString);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private handleError(error: any) {
        if (error.response !== undefined && error.response.status === 400) {
            this.$emit('error', error.response.data);
            return;
        }

        this.$emit('error', error);
    }

    private resourceLoading(params: object) {
        this.$emit('resourceLoading', params);
    }

    private resourcesLoaded(resources: TResourceType[]) {
        this.$emit('resourcesLoaded', resources);
    }

    private resourceLoaded(resource: TResourceType) {
        this.$emit('resourceLoaded', resource);
    }

    private resourceCreated(resource: TResourceType) {
        this.$emit('resourceCreated', resource);
    }

    private resourcePatched(resource: TResourceType) {
        this.$emit('resourcePatched', resource);
    }

    private resourceLoadingFinished(resourceUrl: string) {
        this.$emit('resourceLoadingFinished', resourceUrl);
    }
}
